import { Box, ButtonBase, Stack, styled, Typography } from '@mui/material';
import { EntityIdentifier } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import {
  Editor,
  MentionsPopupState,
  RichTextEditorOptions,
} from '@provision/rich-text-editor';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { Subject } from 'rxjs';
import { MentionPopup } from 'src/components/MentionPopup/mention-popup';
import { REPLY_ROW_PADDING_X } from 'src/components/Replies';
import RichTextEditor from 'src/components/RichTextEditor/rich-text-editor';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { privateNoteButtonStyles } from 'src/pages/Conversations/Chat/shared-styles';
import { ReplyAvatar } from './reply-avatar';

const REPLY_PLACEHOLDER = 'Leave a reply';
const EDITOR_OPTIONS: RichTextEditorOptions = { autofocus: 'end' };
// The editor's own inset would push the text off the column the reply text sits in.
const EDITOR_SX = { paddingLeft: 0, paddingRight: 0 };
const PLACEHOLDER_OPTIONS = {
  placeholder: REPLY_PLACEHOLDER,
  emptyLinePlaceholderText: REPLY_PLACEHOLDER,
};

const ComposerRoot = styled(Stack, { name: 'ReplyComposer' })(({ theme }) => ({
  ...privateNoteButtonStyles(theme),
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, REPLY_ROW_PADDING_X),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const PlaceholderField = styled(ButtonBase, { name: 'ReplyComposerPlaceholder' })(
  ({ theme }) => ({
    flex: 1,
    justifyContent: 'flex-start',
    minHeight: theme.spacing(3),
    color: theme.palette.text.secondary,
  }),
);

interface ReplyComposerProps {
  partnerId: number;
  /** Resolves whether the reply was sent; the draft stays in the editor when it wasn't. */
  onSubmit: (body: string, mentions: EntityIdentifier[]) => Promise<boolean>;
}

/**
 * The "Leave a reply" box under a thread. The editor mounts on first use, so a timeline of
 * notes doesn't carry an editor per note. A send clears the editor at once, as the reply shows
 * in the thread, and keeps a loading Reply button until the server saves it, so quick replies
 * are saved in the order they were sent. A failed send puts the draft back into the editor.
 * A successful send with nothing new typed, or Cancel, unmounts the editor again.
 */
export const ReplyComposer = ({ partnerId, onSubmit }: ReplyComposerProps) => {
  const { user } = useCurrentUserProfile();
  const [isActive, setIsActive] = useState(false);
  const editorRef = useRef<Editor | null>(null);

  const handleActivate = useCallback(() => setIsActive(true), []);
  const handleCancel = useCallback(() => setIsActive(false), []);

  const handleSubmit = useCallback(
    async (body: string, mentions?: EntityIdentifier[]) => {
      if (!body) {
        return;
      }
      editorRef.current?.commands.clearContent(true);
      const isSent = await onSubmit(body, mentions ?? []);
      const editor = editorRef.current;
      if (isSent) {
        if (!editor || editor.isEmpty) {
          setIsActive(false);
        }
        return;
      }
      if (!editor || editor.isDestroyed) {
        return;
      }
      // Text typed while the reply was sending stays after the restored draft.
      if (editor.isEmpty) {
        editor.commands.setContent(body, { emitUpdate: true });
      } else {
        editor.commands.insertContentAt(0, body);
      }
      editor.commands.focus('end');
    },
    [onSubmit],
  );

  const handleGetMentionPopup = useCallback(
    (
      updatePopupStateSubject: Subject<Partial<MentionsPopupState>>,
      onMentionKeyDownRef: MutableRefObject<((event: KeyboardEvent) => boolean) | null>,
    ) => (
      <MentionPopup
        onMentionKeyDownRef={onMentionKeyDownRef}
        updatePopupStateSubject={updatePopupStateSubject}
        partnerId={partnerId}
      />
    ),
    [partnerId],
  );

  const userName = getFullName(user);

  return (
    <ComposerRoot data-testid="reply-composer">
      <ReplyAvatar
        name={userName}
        partnerId={user?.partnerId}
        avatarFileKey={user?.avatarFileKey}
      />
      {isActive ? (
        <Box flex={1} minWidth={0}>
          <RichTextEditor
            onSubmit={handleSubmit}
            editorRef={editorRef}
            submitOnEnter
            buttonText="Reply"
            secondaryButtonText="Cancel"
            secondaryButtonIcon="close"
            secondaryButtonTooltipText="Discard reply"
            handleSecondaryButton={handleCancel}
            editorOptions={EDITOR_OPTIONS}
            placeholderOptions={PLACEHOLDER_OPTIONS}
            withToolBarOtherOptions={false}
            onGetMentionPopup={handleGetMentionPopup}
            sx={EDITOR_SX}
          />
        </Box>
      ) : (
        <PlaceholderField onClick={handleActivate} onFocus={handleActivate}>
          <Typography variant="body2">{REPLY_PLACEHOLDER}</Typography>
        </PlaceholderField>
      )}
    </ComposerRoot>
  );
};
